// Google Fonts loader — injects `<link rel="stylesheet">` into <head> for a
// given (family, weight). Idempotent: same (family, weight) request → single
// <link>. Cache is keyed on the tag's `data-gf-key` so repeated loads across
// mount/unmount cycles reuse the browser's HTTP cache instantly.
//
// This is intentionally NOT reactive — it's a side-effect helper called from
// admin dropdowns (per-option preview) and the public Portfolio page (once,
// on typography-settings-loaded). React state lives in the calling component.

const linkRegistry = new Map(); // key: "Family|weight" -> HTMLLinkElement

const buildCss2Url = (family, weights) => {
  // Google Fonts CSS2 endpoint. Multi-weight syntax: family=Inter:wght@300;400;700
  const familyEncoded = family.replace(/ /g, "+");
  const wghts = Array.isArray(weights) ? weights.join(";") : String(weights);
  return `https://fonts.googleapis.com/css2?family=${familyEncoded}:wght@${wghts}&display=swap`;
};

/**
 * Ensure a Google Font is loaded at the given weight(s).
 * @param {string} family  e.g. "Playfair Display"
 * @param {number|number[]} weights  Single weight or array. Defaults to [400].
 * @returns {HTMLLinkElement | null}
 */
export const loadGoogleFont = (family, weights = [400]) => {
  if (!family) return null;
  const wArr = Array.isArray(weights) ? weights : [weights];
  const key = `${family}|${wArr.join(",")}`;
  if (linkRegistry.has(key)) return linkRegistry.get(key);

  // Also check the DOM in case a previous load happened via another
  // instance (e.g. HMR) — dedupe against existing tags.
  const existing = document.querySelector(`link[data-gf-key="${key}"]`);
  if (existing) {
    linkRegistry.set(key, existing);
    return existing;
  }

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = buildCss2Url(family, wArr);
  link.setAttribute("data-gf-key", key);
  link.setAttribute("data-gf-family", family);
  document.head.appendChild(link);
  linkRegistry.set(key, link);
  return link;
};

/**
 * Load ONLY the 400 weight for a batch of font families — used to preview
 * font names in their own typeface inside dropdowns. Cheap because 400 is
 * cached across sites.
 */
export const loadFontPreview = (family) => loadGoogleFont(family, [400]);
